import { useEffect, useState } from 'react';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import {
  Box,
  Button,
  Grid,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormControl,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Loading } from '../Loading';

interface AssignEntityProps {
  onSubmit: () => void;
  entityId: string;
  entityName: string;
  parentName: string;
}

export const AssignEntity = ({ entityId, entityName, parentName, onSubmit }: AssignEntityProps ) => {
  const api = useApi();
  const [selectedEntityName, setSelectedEntityName] = useState<string>(parentName);
  const [parentEntityNames, setParentEntityNames] = useState<string[]>([]);

  const assignEntity = useMutation(api.assignEntityParentApiV1AssignEntityParentPost, {
    onSuccess() {
      toast.success('Assigned entity successfully.');
      onSubmit();
    },
    onError: apiError,
  });

  const { isLoading, data: prepareEntityNames, } = useQuery(
    ['prepareEntityNames'],
    api.getEntityNamesApiV1GetEntityNamesGet.bind(null, {parent: true}),
    { enabled: true, cacheTime: 0, initialData: undefined },
  );

  useEffect(() => {
    if (prepareEntityNames != null && prepareEntityNames != undefined) {
      setParentEntityNames(prepareEntityNames.data.filter(e => e !== entityName) || []);
    }
  }, [prepareEntityNames]);

  const handleSubmit = () => {
    assignEntity.mutate({
      entity_id: entityId,
      parent_entity_name: selectedEntityName
    });
  };

  const handleChange = ({ target: { value }}: SelectChangeEvent): void => {
    setSelectedEntityName(value);
  };

  if (isLoading) return <Loading />

  return (
    <Box pt={2}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="parent-entity-label">Parent entity name</InputLabel>
            <Select
              labelId="parent-entity-label"
              value={selectedEntityName}
              label="Parent entity name"
              onChange={handleChange}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              { parentEntityNames && parentEntityNames.map((item, i) => {
                return <MenuItem value={item} key={item}>{item}</MenuItem>;
              })}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button variant="contained" onClick={handleSubmit} disabled={entityId === ''}>
          Assign
        </Button>
      </Box>
    </Box>
  );
};

AssignEntity.displayName = 'Assign Entity';
